import { useId } from "react";
import { Link } from "react-router";

import { AuthShell } from "../auth/AuthShell.tsx";
import { PATHS } from "../routes/routes.constants.ts";
import { RESET_COPY } from "./passwordReset.constants.ts";
import { useResetPasswordController } from "./useResetPasswordController.ts";
import "./passwordReset.css";

/**
 * Where the emailed reset link lands.
 *
 * Outside both route gates — see the comment beside it in `AppRoutes.tsx` and the
 * controller's header. The screen itself carries no authority and interprets nothing:
 * the Supabase client consumed the link's fragment before this rendered, so what this
 * has is an ordinary session or no session.
 *
 * The `.reset` wrapper is not decoration. It reuses the sign-in card's field, error and
 * button classes rather than growing a second set, and those are written against a
 * `--role` accent that each login card sets inline. One declaration on the wrapper
 * supplies it; putting it on the `.role__*` classes would override the login cards' own
 * colours, because these stylesheets are global.
 *
 * Every branch is a `kind` on `ResetView`. Four of the five states render nothing but a
 * sentence, which is what keeps this file free of rules.
 */
export function ResetPassword() {
  const id = useId();
  const { view, changePassword, changeConfirmation, submitPassword } =
    useResetPasswordController();

  if (view.kind === "opening") {
    return (
      <AuthShell>
        <div className="reset">
          <h2 className="auth__heading">{RESET_COPY.heading}</h2>
          <p className="auth__subheading" role="status">
            {RESET_COPY.opening}
          </p>
        </div>
      </AuthShell>
    );
  }

  if (view.kind === "unusable") {
    return (
      <AuthShell>
        <div className="reset">
          <h2 className="auth__heading">{RESET_COPY.heading}</h2>
          <p className="role__error" role="alert">
            {RESET_COPY.noSession}
          </p>
          <p className="reset__footer">
            <Link className="login__apply-link" to={PATHS.forgotPassword}>
              {RESET_COPY.askAgain}
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  if (view.kind === "done") {
    return (
      <AuthShell>
        <div className="reset">
          <h2 className="auth__heading">{RESET_COPY.doneHeading}</h2>
          <p className="auth__subheading" role="status">
            {RESET_COPY.done}
          </p>
          {/* To the root, which resolves the workspace — they are already signed in,
              because the recovery link is what signed them in. */}
          <p className="reset__footer">
            <Link className="login__apply-link" to={PATHS.root}>
              {RESET_COPY.carryOn}
            </Link>
          </p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="reset">
        <h2 className="auth__heading">{RESET_COPY.heading}</h2>
        <p className="auth__subheading">{RESET_COPY.blurb}</p>

        <form
          className="reset__form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitPassword();
          }}
        >
          <div className="role__field">
            <label className="role__label" htmlFor={`${id}-password`}>
              {RESET_COPY.passwordLabel}
            </label>
            <input
              id={`${id}-password`}
              className="role__input"
              type="password"
              autoComplete="new-password"
              required
              autoFocus
              disabled={view.kind === "saving"}
              value={view.kind === "setting" ? view.password : ""}
              onChange={(event) => {
                changePassword(event.target.value);
              }}
            />
            <p className="reset__rule">{RESET_COPY.rule}</p>
          </div>

          <div className="role__field">
            <label className="role__label" htmlFor={`${id}-confirm`}>
              {RESET_COPY.confirmLabel}
            </label>
            <input
              id={`${id}-confirm`}
              className="role__input"
              type="password"
              autoComplete="new-password"
              required
              disabled={view.kind === "saving"}
              value={view.kind === "setting" ? view.confirmation : ""}
              onChange={(event) => {
                changeConfirmation(event.target.value);
              }}
            />
            {view.kind === "setting" && view.refusal !== null && (
              <p className="reset__rule">
                {view.refusal === "too-short" ? RESET_COPY.tooShort : RESET_COPY.mismatch}
              </p>
            )}
          </div>

          {/* Said BEFORE they commit, not after. It is a consequence somebody may want to
              prepare for — and it is Supabase's behaviour rather than a choice of ours,
              which settles OND-19's one open decision by reporting it. */}
          <p className="reset__note">{RESET_COPY.willEndSessions}</p>

          {view.kind === "setting" && view.error !== null && (
            <p className="role__error" role="alert">
              {view.error}
            </p>
          )}

          <button
            type="submit"
            className="role__submit"
            disabled={view.kind !== "setting" || !view.canSubmit}
          >
            {view.kind === "saving" ? RESET_COPY.submitting : RESET_COPY.submit}
          </button>
        </form>
      </div>
    </AuthShell>
  );
}
